import { useMemo } from 'react';
import { buildReportsViewModel } from '@/application/reports';
import { useExpenses } from '@/hooks/useExpenses';
import { Donut, MiniBars } from '@/components/hearthline/charts';
import { CategoryChip } from '@/components/hearthline/primitives';
import { Icons } from '@/components/icons';
import { fmtUSD, fmtUSD0 } from '@/lib/format';

const pct = (part: number, whole: number) => (whole > 0 ? ((part / whole) * 100).toFixed(0) : '0');

export function ReportsRoute() {
  const { expenses, loading } = useExpenses();
  const vm = useMemo(() => buildReportsViewModel(expenses), [expenses]);

  if (loading) return <div className="empty"><div className="ttl">Loading reports…</div></div>;

  if (expenses.length === 0) {
    return (
      <div className="empty">
        <Icons.Chart size={32} stroke="var(--faint)" />
        <div className="ttl">No data yet</div>
        <div>Reports appear once you've logged some receipts.</div>
      </div>
    );
  }

  const { byCategory, categoryTotal, status, monthlyTrend, topItems } = vm;

  const statusRows = [
    { id: 'pending', label: 'Pending reimbursement', color: 'var(--pending)', v: status.pending, n: status.pendingCount },
    { id: 'reimbursed', label: 'Reimbursed', color: 'var(--reimbursed)', v: status.reimbursed, n: status.reimbursedCount },
    { id: 'donated', label: 'Donated', color: 'var(--donated)', v: status.donated, n: status.donatedCount },
  ];

  return (
    <>
      <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="card metric">
          <div className="lbl">Total tracked</div>
          <div className="val">{fmtUSD0(status.total)}</div>
          <div className="delta">{vm.receiptCount} receipts</div>
        </div>
        <div className="card metric">
          <div className="lbl">Reimbursed</div>
          <div className="val" style={{ color: 'oklch(0.45 0.10 150)' }}>{fmtUSD0(status.reimbursed)}</div>
          <div className="delta">{pct(status.reimbursed, status.total)}% of total</div>
        </div>
        <div className="card metric">
          <div className="lbl">Community donations</div>
          <div className="val" style={{ color: 'oklch(0.42 0.10 320)' }}>{fmtUSD0(status.donated)}</div>
          <div className="delta">{pct(status.donated, status.total)}% of total</div>
        </div>
      </div>

      <div className="report-grid">
        <div className="card">
          <div className="card-h">
            <h3>Spend by category</h3>
            <span className="sub">{byCategory.length} categories used</span>
          </div>
          <div className="donut-wrap">
            <div className="donut">
              <Donut slices={byCategory.map((c) => ({ value: c.total, color: c.color }))} />
              <div className="center">
                {fmtUSD0(categoryTotal)}
                <div className="ct-lbl">all categories</div>
              </div>
            </div>
            <div className="donut-legend">
              {byCategory.map((c) => (
                <div key={c.category} className="dl-row">
                  <span className="sw" style={{ background: c.color }} />
                  <span className="nm">{c.label}</span>
                  <span className="amt">{fmtUSD(c.total)}</span>
                  <span className="pct">{pct(c.total, categoryTotal)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-h">
            <h3>Status breakdown</h3>
            <span className="sub">Reimbursement health</span>
          </div>
          <div style={{ padding: 18 }}>
            {statusRows.map((s) => (
              <div key={s.id} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                    <span style={{ fontSize: 13 }}>{s.label}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>· {s.n} receipts</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(s.v)}</div>
                </div>
                <div style={{ height: 8, background: 'var(--bg-2)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct(s.v, status.total)}%`, background: s.color, borderRadius: 4 }} />
                </div>
              </div>
            ))}

            <div style={{ marginTop: 14, padding: 12, background: 'var(--bg-2)', borderRadius: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
              <div style={{ fontSize: 10.5, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Reminder</div>
              {status.pendingCount} pending {status.pendingCount === 1 ? 'receipt is' : 'receipts are'} awaiting reimbursement — that's {fmtUSD(status.pending)} owed to volunteers.
            </div>
          </div>
        </div>

        <div className="card full" style={{ gridColumn: '1 / -1' }}>
          <div className="card-h">
            <h3>Spend trend</h3>
            <span className="sub">Last 12 months</span>
          </div>
          <div className="trend">
            <MiniBars data={monthlyTrend} />
          </div>
        </div>

        <div className="card full" style={{ gridColumn: '1 / -1' }}>
          <div className="card-h">
            <h3>Top items by spend</h3>
            <span className="sub">Across all receipts</span>
          </div>
          <div className="card-b tight">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th className="num">Units purchased</th>
                  <th className="num">Total spent</th>
                </tr>
              </thead>
              <tbody>
                {topItems.map((it, i) => (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td className="store">{it.name}</td>
                    <td><CategoryChip category={it.category} /></td>
                    <td className="num dt">{it.qty}</td>
                    <td className="num" style={{ fontWeight: 500 }}>{fmtUSD(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
